import { Button, WithActions, PlusSmall, Minus } from "components";
import { t } from "i18next";

interface FormData {
  parking_name_mk: string;
  parking_name_al: string;
  parking_name_en: string;
  longitude: string;
  latitude: string;
  municipality_id: number;
  area_type: string;
  total_parking_slots: number;
  payment_options: number[];
  amenities: number[];
}

interface PaymentAmenitiesStepProps {
  form: FormData;
  handleChange: (key: keyof FormData, value: string | number | number[]) => void;
  errorMessage: string | null;
  onClose: () => void;
  prevStep: () => void;
  nextStep: () => void;
  isLoading: boolean;
  getParkingNameByLanguage: () => string;
}

export const PaymentAmenitiesStep: React.FC<PaymentAmenitiesStepProps> = ({
  form,
  handleChange,
  errorMessage,
  onClose,
  prevStep,
  nextStep,
  isLoading,
  getParkingNameByLanguage,
}) => {
  const toggleItem = (key: keyof FormData, id: number) => {
    const current = form[key] as number[];
    const updated = current.includes(id) ? current.filter((val) => val !== id) : [...current, id];
    handleChange(key, updated);
  };

  return (
    <div>
      <h3 className="text-xs font-light uppercase text-primary">
        {t("parkingManagement.configureParking")} ({getParkingNameByLanguage() || "PARKING NAME"})
      </h3>

      <div className="mt-2 space-y-4">
        <div>
          <h4 className="font-semibold">{t("parkingManagement.paymentOptions")}*</h4>
          <div className="flex flex-wrap gap-4">
            {[
              { id: 1, label: "Cash" },
              { id: 2, label: "Card" },
            ].map(({ id, label }) => {
              const selected = form.payment_options.includes(id);
              const baseClasses = "flex items-center border border-[#1C67AD] rounded min-w-[120px] h-[48px]";
              const iconContainer = `flex h-full w-[40px] items-center justify-center ${
                selected ? "bg-green-700" : "bg-primary"
              }`;
              return (
                <button key={id} onClick={() => toggleItem("payment_options", id)} className={baseClasses}>
                  <div className="flex-1 px-4 text-left font-semibold text-[#1C67AD]">{label}</div>
                  <div
                    className={`flex h-full w-[40px] items-center justify-center ${
                      selected ? "bg-green-700" : "bg-primary"
                    }`}
                  >
                    {selected ? <Minus className="text-white" /> : <PlusSmall className="text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-semibold">{t("parkingManagement.amenities")}</h4>
          <div className="flex flex-wrap gap-4">
            {[
              { id: 1, label: "Wheelchair" },
              { id: 2, label: "Cameras" },
              { id: 3, label: "Fire Alarm" },
            ].map(({ id, label }) => {
              const selected = form.amenities.includes(id);
              const baseClasses = "flex items-center border border-[#1C67AD] rounded min-w-[140px] h-[48px]";
              const iconContainer = `flex h-full w-[40px] items-center justify-center ${
                selected ? "bg-green-700" : "bg-primary"
              }`;
              return (
                <button key={id} onClick={() => toggleItem("amenities", id)} className={baseClasses}>
                  <div className="flex-1 px-4 text-left font-semibold text-[#1C67AD]">{label}</div>
                  <div
                    className={`flex h-full w-[40px] items-center justify-center ${
                      selected ? "bg-green-700" : "bg-primary"
                    }`}
                  >
                    {selected ? <Minus className="text-white" /> : <PlusSmall className="text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}

      <WithActions
        closeButton={t("common.cancel")}
        setOpen={onClose}
        additionalButtons={
          <>
            <Button onClick={prevStep}>{t("common.back")}</Button>
            <Button onClick={nextStep} disabled={isLoading}>
              {isLoading ? t("parkingManagement.saving") : t("common.continue")}
            </Button>
          </>
        }
      />
    </div>
  );
};
